import React from "react";
import PropTypes from "prop-types";
import { Table } from "reactstrap";
import { Checkbox } from "./Checkbox";

/**
 * 勾選矩陣（design system primitive，模板 Tables + form-check）。
 *
 * 列 × 欄的勾選表:角色權限矩陣(模組 × 動作)是第一個使用者。
 * 值是 `{ 列key: [欄key, ...] }` —— 直接可送 API,不用再轉一次。
 *
 *   <CheckMatrix
 *     rows={[{ key: "cabinet", label: "機櫃" }]}
 *     columns={[{ key: "read", label: "查詢" }, { key: "create", label: "新增" }]}
 *     value={{ cabinet: ["read"] }} onChange={setPerms}
 *   />
 *
 * 支援整列/整欄全選(再按一次取消)、顯示已選數量、readOnly 唯讀檢視。
 */
const cellsOf = (value, rowKey) => value[rowKey] || [];

export const CheckMatrix = ({
  rows = [], columns = [], value = {}, onChange, readOnly = false,
  rowHeader = "項目", showCount = true,
}) => {
  const has = (rowKey, colKey) => cellsOf(value, rowKey).includes(colKey);
  const total = rows.reduce((n, r) => n + cellsOf(value, r.key).length, 0);

  const emit = (next) => { if (onChange && !readOnly) onChange(next); };

  const toggleCell = (rowKey, colKey) => {
    const current = cellsOf(value, rowKey);
    const next = has(rowKey, colKey)
      ? current.filter((c) => c !== colKey)
      : [...current, colKey];
    const merged = { ...value, [rowKey]: next };
    if (!next.length) delete merged[rowKey]; // 空陣列就別留著,值才乾淨
    emit(merged);
  };

  // 用「每個欄位都有」判斷,不用長度 —— 值裡混進已移除的欄位 key 時長度會騙人
  const rowFull = (rowKey) => columns.length > 0 && columns.every((c) => has(rowKey, c.key));
  const colFull = (colKey) => rows.length > 0 && rows.every((r) => has(r.key, colKey));

  const rowAll = (rowKey) => {
    const full = rowFull(rowKey);
    const merged = { ...value };
    if (full) delete merged[rowKey];
    else merged[rowKey] = columns.map((c) => c.key);
    emit(merged);
  };

  const colAll = (colKey) => {
    const full = colFull(colKey);
    const merged = { ...value };
    rows.forEach((r) => {
      const next = full
        ? cellsOf(value, r.key).filter((c) => c !== colKey)
        : [...new Set([...cellsOf(value, r.key), colKey])];
      if (next.length) merged[r.key] = next;
      else delete merged[r.key];
    });
    emit(merged);
  };

  // 勾選框走共用的 <Checkbox>(Figma `checkbox_icons`),半選由它處理 ——
  // 這裡原本自己用 ref 設 indeterminate,和 Checkbox 是同一份邏輯,收斂掉。
  const box = ({ partial, onChange, ...props }) => (
    <Checkbox
      disabled={readOnly}
      indeterminate={partial}
      onChange={() => onChange()}
      {...props}
    />
  );

  return (
    <div>
      <Table className="table-nowrap align-middle mb-2" bordered responsive>
        <thead className="table-light">
          <tr>
            <th>{rowHeader}</th>
            {columns.map((c) => (
              <th key={c.key} className="text-center">
                <div className="d-flex flex-column align-items-center gap-1">
                  <span>{c.label}</span>
                  {/* 唯讀時不放全選鍵:按不動的勾選框只會讓人以為壞了 */}
                  {readOnly ? null : box({
                    "aria-label": `${c.label} 整欄全選`,
                    checked: colFull(c.key),
                    partial: rows.some((r) => has(r.key, c.key)),
                    onChange: () => colAll(c.key),
                  })}
                </div>
              </th>
            ))}
            {readOnly ? null : <th className="text-center">整列</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>{r.label}</td>
              {columns.map((c) => (
                <td key={c.key} className="text-center">
                  {box({
                    "aria-label": `${r.label}－${c.label}`,
                    checked: has(r.key, c.key),
                    onChange: () => toggleCell(r.key, c.key),
                  })}
                </td>
              ))}
              {readOnly ? null : (
                <td className="text-center">
                  {box({
                    "aria-label": `${r.label} 整列全選`,
                    checked: rowFull(r.key),
                    partial: cellsOf(value, r.key).length > 0,
                    onChange: () => rowAll(r.key),
                  })}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
      {showCount ? <div className="text-muted small">已選 {total} 項</div> : null}
    </div>
  );
};
CheckMatrix.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.node })),
  columns: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.node })),
  value: PropTypes.object,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  rowHeader: PropTypes.node,
  showCount: PropTypes.bool,
};

export default CheckMatrix;
